import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { AdminPageHeader, AdminKpi, AdminKpiStrip } from '../../components/admin/AdminPageHeader';
import AdminToolbar from '../../components/admin/AdminToolbar';
import {
  Plus, Search, Briefcase, Users, TrendingUp, Layers,
  Link as LinkIcon, Filter, X, LayoutGrid, Table as TableIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { InstrumentCard, InstrumentsTable } from '../../components/admin/instruments/InstrumentCard';
import { InstrumentFormDialog } from '../../components/admin/instruments/InstrumentFormDialog';

export default function AdminInstrumentsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [instruments, setInstruments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOffering, setFilterOffering] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [sort, setSort] = useState('newest');
  const [viewMode, setViewMode] = useState('cards');

  const [showDialog, setShowDialog] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [instrumentsRes, projectsRes] = await Promise.all([
        api.get('/admin/projects/instruments/all'),
        api.get('/admin/projects').catch(() => ({ data: { projects: [] } }))
      ]);
      setInstruments(instrumentsRes.data.instruments || []);
      setProjects(projectsRes.data.projects || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load instruments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditingInstrument(null);
    setShowDialog(true);
  };

  const openEdit = (instrument) => {
    setEditingInstrument(instrument);
    setShowDialog(true);
  };

  const projectName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.title || project?.name || '';
  };

  const projectOptions = [...new Map(
    instruments.filter((i) => i.project_id).map((i) => [i.project_id, projectName(i.project_id) || i.project_id])
  )].map(([value, label]) => ({ value, label }));

  const fundedPct = (i) => {
    const authorized = Number(i.total_authorized_units || 0);
    return authorized > 0 ? (Number(i.units_outstanding || 0) / authorized) * 100 : 0;
  };

  const filteredInstruments = (() => {
    const q = searchQuery.trim().toLowerCase();
    let list = instruments.filter((i) => {
      if (q) {
        const haystack = [i.name, i.code, projectName(i.project_id)].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (filterStatus !== 'all' && i.status !== filterStatus) return false;
      if (filterOffering !== 'all' && i.offering_status !== filterOffering) return false;
      if (filterProject !== 'all' && i.project_id !== filterProject) return false;
      return true;
    });
    if (sort === 'funded') list = [...list].sort((a, b) => fundedPct(b) - fundedPct(a));
    return list;
  })();

  const getProjectName = (projectId) => {
    if (!projectId) return null;
    const project = projects.find(p => p.id === projectId);
    return project?.title || project?.name || projectId;
  };

  const stats = {
    total: instruments.length,
    active: instruments.filter(i => i.status === 'active').length,
    totalUnitsAuthorized: instruments.reduce((sum, i) => sum + (i.total_authorized_units || 0), 0),
    totalUnitsSubscribed: instruments.reduce((sum, i) => sum + (i.units_subscribed || 0), 0),
    linked: instruments.filter(i => i.project_id).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-9 w-9 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] space-y-6" data-testid="admin-instruments-page">
      <AdminPageHeader
        eyebrow="Capital"
        title="Instruments"
        subtitle="Units, pricing, subscription terms and funding progress of every offering."
        actions={(
          <>
            <div className="flex items-center rounded-lg border border-black/[0.08] p-0.5">
              <Button
                size="sm"
                variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                onClick={() => setViewMode('cards')}
                data-testid="view-mode-cards"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                onClick={() => setViewMode('table')}
                data-testid="view-mode-table"
              >
                <TableIcon className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/instruments/new')} data-testid="wizard-btn">
              <Plus className="h-4 w-4 md:mr-1.5" />
              <span className="hidden md:inline">Guided wizard</span>
            </Button>
            <Button size="sm" onClick={openCreate} data-testid="create-instrument-btn">
              <Plus className="h-4 w-4 md:mr-1.5" />
              <span className="hidden md:inline">Quick create</span>
            </Button>
          </>
        )}
      />

        <AdminKpiStrip>
          <AdminKpi label="Total" value={stats.total} />
          <AdminKpi label="Active" value={stats.active} />
          <AdminKpi label="Authorized" value={stats.totalUnitsAuthorized.toLocaleString()} />
          <AdminKpi label="Subscribed" value={stats.totalUnitsSubscribed.toLocaleString()} />
          <AdminKpi label="Linked" value={stats.linked} />
        </AdminKpiStrip>


      <AdminToolbar
        search={searchQuery}
        onSearch={setSearchQuery}
        searchPlaceholder="Search by name, code or project…"
        filters={[
          { key: 'status', label: 'Status', options: [
            { value: 'draft', label: 'Draft' },
            { value: 'active', label: 'Active' },
            { value: 'frozen', label: 'Frozen' },
            { value: 'archived', label: 'Archived' },
          ] },
          { key: 'offering', label: 'Offering', options: [
            { value: 'draft', label: 'Draft' },
            { value: 'open', label: 'Open' },
            { value: 'closed', label: 'Closed' },
          ] },
          { key: 'project', label: 'Project', options: projectOptions },
        ]}
        values={{ status: filterStatus, offering: filterOffering, project: filterProject }}
        onFilter={(key, value) => {
          if (key === 'status') setFilterStatus(value);
          else if (key === 'offering') setFilterOffering(value);
          else setFilterProject(value);
        }}
        onClear={() => { setFilterStatus('all'); setFilterOffering('all'); setFilterProject('all'); }}
        sortOptions={[
          { value: 'newest', label: 'Newest first' },
          { value: 'funded', label: 'Most funded' },
        ]}
        sort={sort}
        onSort={setSort}
        view={viewMode === 'cards' ? 'card' : 'list'}
        onView={(v) => setViewMode(v === 'card' ? 'cards' : 'table')}
        resultLabel={`${filteredInstruments.length} of ${instruments.length} instruments`}
      />

      {/* Instruments */}
      {filteredInstruments.length === 0 ? (
          <Card className="bg-slate-50 border-dashed p-12 text-center">
            <Briefcase className="h-12 w-12 text-foreground-muted mx-auto mb-4" />
            <h3 className="font-medium mb-2">
              {instruments.length === 0 ? 'No Instruments Yet' : 'No Matching Instruments'}
            </h3>
            <p className="text-foreground-muted text-sm mb-4">
              {instruments.length === 0
                ? 'Create your first financial instrument to get started.'
                : 'Try adjusting your search or filters.'}
            </p>
            {instruments.length === 0 && (
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create Instrument
              </Button>
            )}
          </Card>
        ) : viewMode === 'table' ? (
          <InstrumentsTable
            instruments={filteredInstruments}
            getProjectName={getProjectName}
            onEdit={openEdit}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInstruments.map((instrument) => (
              <InstrumentCard
                key={instrument.id}
                instrument={instrument}
                projectName={getProjectName(instrument.project_id)}
                onEdit={openEdit}
                onRefresh={fetchData}
              />
            ))}
          </div>
      )}

      <InstrumentFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        instrument={editingInstrument}
        projects={projects}
        onSaved={fetchData}
      />
    </div>
  );
}
